import React, { useEffect, useState } from "react";
import { Box, Button, Paper, Grid, useTheme, Typography, CircularProgress, Select, MenuItem, Stack, Switch, alpha, Tooltip, TextField, FormControl, InputLabel } from "@mui/material";
import AxionControls from "../../../components/controls/AxionControls";
import GridDateTimePicker from "../../home/components/GridDateTimePicker";
import messages from "../messages";
import { useMsal, useAccount } from "@azure/msal-react";
import { tokens } from "../../../assets/theme";
import { FormattedMessage, useIntl } from "react-intl";
import { updatePO, getItemMachinesForItem } from "../../../fetchers/productionPlan";
import { ReplanMachine2, ReCalculateEndTime, ReCalculateEndTimeForNewOrder } from "./planningFunctions";
import { getProductionPlanInfo } from "../../../fetchers/productionPlan";
import { getProductionOrderOee } from "../../../fetchers/productionOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import dayjs from "dayjs";
import { format, set } from "date-fns";
import { InfoOutlined } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";

export default function PlanUpdatePODialog(props) {

    //#### Hårdkodningar #############################
    const gapInMinutes = 120;
    const defaultOee = 0.8;
    //#################################

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { formatMessage } = useIntl();
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);

    const [quantity, setQuantity] = React.useState(props.operation?.plannedQty);
    const [startTime, setStartTime] = React.useState(new Date(props.operation?.plannedOrReplannedStartTime));
    const [productionOrderId, setProductionOrderId] = React.useState(props.operation?.productionOrderId);
    const [productId, setProductId] = React.useState(props.operation?.itemNo);
    const [machineId, setMachineId] = React.useState(props.operation?.machineId);
    const [buttonDisabled, setButtonDisabled] = React.useState(false);
    const [itemMachines, setItemMachines] = React.useState([]);
    const [tak, setTak] = React.useState(props.operation?.oeeValue || defaultOee);
    const [toolId, setToolId] = React.useState(null);
    const [stockBalance, setStockBalance] = React.useState(0);
    const [reservedBalance, setReservedBalance] = React.useState(0);
    const [idealCycleTime, setIdealCycleTime] = React.useState(60);
    const [historicOeeFound, setHistoricOeeFound] = React.useState(false);
    const [oeeAvg, setOeeAvg] = React.useState(null);
    const [oeeAvgCount, setOeeAvgCount] = React.useState(0);
    const [oeeHistoric, setOeeHistoric] = React.useState(null);
    const [manualOee, setManualOee] = React.useState(defaultOee);
    const [oeeType, setOeeType] = React.useState(props.operation?.oeeCalculationType || "default");
    const [showManualOeeInput, setShowManualOeeInput] = React.useState(false);
    const [oeeInitializedFromPlan, setOeeInitializedFromPlan] = React.useState(false);
    const speedUnitTypeId = props.operation.speedUnitTypeId;
    const isoFormat = "YYYY-MM-DDTHH:mm:ss";
    const [manualChange, setManualChange] = React.useState(false);
    const language = localStorage.getItem('language') || 'en';

    //console.log("PlanUpdatePODialog props:", props.operation.speedUnitTypeId);



    let { calcEndTime, idealTimeToEnd, idealRunTime } = { calcEndTime: null, idealTimeToEnd: 0, idealRunTime: 0 };

    //let { calcEndTime, idealTimeToEnd, idealRunTime } =ReCalculateEndTime(props.operation, props.operation?.item, itemOEE);

    // if (props.recalculateAffectedOrders !== "true") {
    //     let duration = dayjs(props.operation?.plannedEndTime).diff(dayjs(props.operation?.plannedStartTime), 'minute');
    //     calcEndTime = dayjs(startTime).add(duration, 'minute').format(isoFormat);
    //     idealTimeToEnd = duration;
    //     idealRunTime = duration;
    // }

    let machineName = getMachineNameById(machineId);


    // const productionPlanInfoQuery = useQuery(
    //     ["productionPlanInfo", productionOrderId],
    //     () => getProductionPlanInfo(props.operation?.productionOrderId, companyKey),
    //     {}
    // );

    useEffect(() => {
        setOeeInitializedFromPlan(false);
    }, [productionOrderId]);


    useEffect(() => {

        if (oeeType === "historic avg") {
            //console.log("OEE type is historic avg, skipping item machines fetch and OEE data set");
            setOeeAvg(tak);
        }

        let machines = props.machineQuery || [];
        

        //fetch item machines
        //console.log("!!Fetching item machines for item:", props?.operation?.itemNo);
        getItemMachinesForItem(props?.operation?.itemNo, companyKey).then((result) => {
            //console.log("Item machines fetched:", result);
            result.forEach((machine) => {
                machine.id = getMachineIdByName(machine.machineName);
            });
        
            //get mchines not in result
            let otherMachines = machines.filter(m => !result.some(r => r.machineName === m.machineName));

            result = result.concat(otherMachines);

            //console.log ("Final machine list for item:", result);

            let finalResult = result.map((machine) => ({
                ...machine,
                id: machine.id,
                machineName: machine.machineName,
                abC_Class: machine.abC_Class ? machine.abC_Class : "",
            }));
        
            setItemMachines(finalResult);
            setOEEDataForMachineAndProduct(machineId, productId, finalResult, oeeType, manualOee);
        }).catch((error) => {
            //console.log("Could not fetch item machines:", error);
        });
    }, []);
    //}, [props?.operation?.itemNo]);

    useEffect(() => {
        if (oeeInitializedFromPlan ) {
            return;
        }

        const planOeeValue = props.operation?.oeeValue;
        const planOeeType = props.operation?.oeeCalculationType;

        const normalizedOeeValue = typeof planOeeValue === "number"
            ? (planOeeValue > 1 ? planOeeValue / 100 : planOeeValue)
            : null;

        let resolvedOeeType = oeeType;
        if (typeof planOeeType === "string") {
            const normalizedType = planOeeType.toLowerCase();
            if (normalizedType.includes("historic") && normalizedType.includes("avg")) {
                resolvedOeeType = "historic avg";
            } else if (normalizedType.includes("historic")) {
                resolvedOeeType = "historic";
            } else if (normalizedType.includes("manual")) {
                resolvedOeeType = "manual";
            } else if (normalizedType.includes("default")) {
                resolvedOeeType = "default";
            }
        }

        setOeeType(resolvedOeeType);

        if (resolvedOeeType === "manual") {
            setManualOee(normalizedOeeValue);
            setShowManualOeeInput(true);
        }

        if (resolvedOeeType === "historic") {
            setOeeHistoric(normalizedOeeValue);
        }

        if (resolvedOeeType === "historic avg") {
            setOeeAvg(normalizedOeeValue);
        }

        if (normalizedOeeValue !== null) {
            //console.log("Setting TAK from plan OEE value (normalizedOeeValue):", normalizedOeeValue, "resolvedOeeType:", resolvedOeeType);
            setTak(normalizedOeeValue);
        }

        setOeeInitializedFromPlan(true);
    }, [oeeInitializedFromPlan, oeeType]);

    useEffect(() => {
        if (manualChange === false) {
            setManualChange(true);
            return;
        }

        let resultOee = defaultOee;

        switch (oeeType) {
            case "manual":
                resultOee = manualOee;
                break;
            case "historic":
                resultOee = oeeHistoric;
                break;
            case "historic avg":
                resultOee = oeeAvg;
                break;
            case "default":
                resultOee = defaultOee;
                break;
            default:
                resultOee = defaultOee;
                break;
        }

        if (isNaN(resultOee) || resultOee === null || resultOee === undefined) {
            resultOee = defaultOee;
        }

        setTak(resultOee);
    }, [oeeType, manualOee, oeeHistoric, oeeAvg, defaultOee]);


    function getHourAndMinutesFromMinutes(totalMinutes) {
        if (isNaN(totalMinutes) || totalMinutes === null || totalMinutes === undefined || totalMinutes <= 0) {
            return { hours: "-", minutes: "-" };
        }
        const hours = (Math.floor(totalMinutes / 60)).toFixed(0);
        const minutes = (totalMinutes % 60).toFixed(0);
        return { hours, minutes };
    }

    function getMachineNameById(id) {
        const machine = props.machineQuery?.find((machine) => machine.id === id);
        return machine ? machine.machineName : "";
    }

    function getMachineIdByName(name) {
        const machine = props.machineQuery?.find((machine) => machine.machineName === name);
        return machine ? machine.id : "";
    }

    function formatDateAndFullTime(date) {
        return dayjs(date).format(isoFormat);
    }
    function isDateValid(dateStr) {
        return !isNaN(new Date(dateStr));
    }
    const handleStartTimeChange = (event) => {
        let startDate = startTime;
        if (isDateValid(event)) {
            startDate = dayjs(event).format(isoFormat);
        } else {
            //console.log("!!!Invalid date:", event);
            startDate = startTime;
        }
        //console.log("!!!!Start time changed:", startDate);
        setStartTime(startDate);
    }
    // const handleStartTimeChange = (newValue) => {
    //     setStartTime(newValue);
    // }
    const handleQuantityChange = (event) => {
        if (isNaN(event.target.value)) {
            event.target.value = quantity;
        } else {
            setQuantity(event.target.value);
        }

        //###setOEEDataForMachineAndProduct(machineId, productId, itemMachines, oeeType, manualOee);
    }
    const handleMachineChange = (event) => {
        let machId = getMachineIdByName(event.target.value);
        setMachineId(machId);

        setOEEDataForMachineAndProduct(machId, productId, itemMachines, oeeType, manualOee);
    }

    const handleOeeTypeChange = (event, newValue) => {
        const nextOeeType = event.target.value;

        setOeeType(nextOeeType);

        if (nextOeeType === "manual") {
            setShowManualOeeInput(true);
        } else {
            setShowManualOeeInput(false);
        }

        //###setOEEDataForMachineAndProduct(machineId, productId, itemMachines, nextOeeType, manualOee);
    }

    const handleManualOeeChange = (event) => {
        const rawValue = event.target.value;

        if (rawValue === "") {
            setManualOee(null);
            setOeeType("default");
            setShowManualOeeInput(true);
            return;
        }

        if (isNaN(rawValue)) {
            return;
        }

        let value = parseFloat(rawValue);
        if (value < 0 || value > 100) {
            return;
        }

        value = value / 100;
        setManualOee(value);
        

        if (oeeType !== "manual") {
            setOeeType("manual");
            setShowManualOeeInput(true);
        }
    }

    const handleSnapChange = (event) => {
        props.setSnapToPrevious(event.target.checked.toString());
        //console.log("Snap to previous changed:", event.target.checked);

    }
    const handleReplanChange = (event) => {
        props.setReplanOverlaps(event.target.checked.toString());
    }
    const handleRecalculateChange = (event) => {
        props.setRecalculateAffectedOrders(event.target.checked.toString());
    }

    const setOEEDataForMachineAndProduct = (machId, prodId, machinesForItem, oeeTypeOverride, manualOeeOverride) => {

        let machName = getMachineNameById(machId);
        let selectedMachine = machinesForItem.find(m => m.machineName === machName);
        
        setStockBalance(selectedMachine?.stockBalance || 0);
        setReservedBalance(selectedMachine?.reservedBalance || 0);
        setToolId(selectedMachine?.toolId || null);
        setIdealCycleTime(selectedMachine?.idealCycleTime || 60);

        getProductionOrderOee(machId, prodId, companyKey).then((oeeData) => {
            // console.log("OEE data for product:", oeeData);

            let localOeeAvgCalc = null;
            let localOeeHistoric = null;

            if (Array.isArray(oeeData)) {
                let ordersWithOee = oeeData.filter(x => x.tak !== undefined && x.tak !== null);
                let lengthWithOee = ordersWithOee.length;
                localOeeAvgCalc = lengthWithOee > 0 ? ordersWithOee.reduce((sum, record) => sum + (record.tak || 0), 0) / lengthWithOee / 100 : null;
                localOeeHistoric = (oeeData.length > 0 && oeeData[0].tak !== undefined && oeeData[0].tak !== null) ? oeeData[0].tak / 100 : null;
                setOeeAvgCount(lengthWithOee);
            } else if (oeeData && oeeData.tak !== undefined && oeeData.tak !== null) {
                localOeeHistoric = oeeData.tak / 100;
            }

            setOeeHistoric(localOeeHistoric);
            setOeeAvg(localOeeAvgCalc);

            let resultOee = defaultOee;
            const resolvedOeeType = oeeTypeOverride || oeeType;
            const resolvedManualOee = manualOeeOverride !== undefined ? manualOeeOverride : manualOee;

            switch (resolvedOeeType) {
                case "manual":
                    resultOee = resolvedManualOee;
                    break;
                case "historic":
                    resultOee = localOeeHistoric;
                    break;
                case "historic avg":
                    resultOee = localOeeAvgCalc;
                    break;
                case "default":
                    resultOee = defaultOee;
                    break;
                default:
                    resultOee = defaultOee;
                    setOeeType("default");
                    break;
            }

            if (isNaN(resultOee) || resultOee === null || resultOee === undefined) {
                resultOee = defaultOee;
                setOeeType("default");
            }

            setTak(resultOee);

            if (localOeeHistoric !== null) {
                setHistoricOeeFound(true);
            } else {
                setHistoricOeeFound(false);
            }
        }
        ).catch((error) => {
            console.log("Could not fetch OEE data for product", error);
            setTak(defaultOee);
            setHistoricOeeFound(false);
        });
    }

    const handleUpdatePO = () => {
        setButtonDisabled(true);
        props.setShowBackdrop(true);

        //console.log("Updating PO with:", productId, machineId, startTime, quantity, tak);

        let machineData = props.plannedData[0]?.children?.filter(machine => machine.id === machineId)
        let machineName = getMachineNameById(machineId);


        //console.log ("Updating- ReCalculateEndTimeForNewOrder:", startTime, quantity, props.operation?.reportedQty || 0, tak, speedUnitTypeId, props.operation?.idealCycleTime);

        let res = ReCalculateEndTimeForNewOrder(startTime, quantity, props.operation?.reportedQty || 0, tak, speedUnitTypeId, props.operation?.idealCycleTime);

        calcEndTime = res.calcEndTime;
        idealTimeToEnd = res.idealTimeToEnd;
        idealRunTime = res.idealRunTime;

        //console.log ("Updating- ReCalculateEndTimeForNewOrder result:", res);

        let updPO = {
            ...props.operation,
            "start": formatDateAndFullTime(startTime),
            "end": formatDateAndFullTime(calcEndTime),
            // "plannedStartTime": formatDateAndFullTime(period.plannedStartTime),
            // "plannedEndTime": formatDateAndFullTime(period.plannedEndTime),
            "replannedStartTime": formatDateAndFullTime(startTime),
            "replannedEndTime": formatDateAndFullTime(calcEndTime),
            "plannedOrReplannedStartTime": formatDateAndFullTime(startTime),
            "plannedOrReplannedEndTime": formatDateAndFullTime(calcEndTime),
            "plannedQty": quantity,
            "machineId": machineId,
            "oeeValue": tak,
            "oeeCalculationType": oeeType,
            "companyId": companyKey,
        }

        //check if order overlaps existing orders on the same machine?

        let replannedData = ReplanMachine2(updPO, machineData, machineName, gapInMinutes, props.snapToPrevious, props.replanOverlaps, props.recalculateAffectedOrders, companyKey, props.scheduleRows, props.historicOeeData, defaultOee); //    return {replannedOrder: updatePOData, replannedOrders: periods};

        replannedData.replannedOrders.forEach((period) => {

            period.edit = props.edit === "true" ? true : false;

            updatePO(period).then((result) => {

                //console.log("PO updated:", result?.data?.manufacturingOrder?.moNo, result);

                if (result.status) {
                     //console.log ("Order updated successfully in ERP system, refreshing data...", result?.data);
                    pushNotification("Production plan updated. ");
                } else {
                    pushErrorMessage("Could not update plan: " + result?.message);
                }

            }).catch((error) => {
                console.log("Could not update plan in erp system", error);
                pushErrorMessage("Could not update plan in erp system", error);
                props.setShowBackdrop(false);
                setButtonDisabled(false);
            });


        });

        replannedData.replannedOrder.edit = props.edit === "true" ? true : false;

        props.setShowBackdrop(true);

        updatePO(replannedData.replannedOrder).then((result) => {

            if (result.status) {
                pushNotification("Moved Production plan updated: " + (result?.data?.manufacturingOrder?.moNo || replannedData.replannedOrder.productionOrderNo));
            } else {
                pushErrorMessage("Could not move plan: " + result?.message);
            }

            props.handleRefreshData();
            props.setShowBackdrop(false);
            setButtonDisabled(false);
            props.setOpenUpdatePopup(false);
        }).catch((error) => {
            console.log("Could update order", error);
            pushErrorMessage("Could not update order:   " + error);
            setButtonDisabled(false);
            props.handleRefreshData();
            props.setShowBackdrop(false);
            props.setOpenUpdatePopup(false);
        });
    }

    let localIdealTimeToEnd = ReCalculateEndTimeForNewOrder(startTime, quantity, props.operation.reportedQty || 0, tak, speedUnitTypeId, props.operation.idealCycleTime).idealTimeToEnd;
    let oeeTypeLabel = "Default OEE";

    switch (oeeType) {
        case "historic":
            oeeTypeLabel = "Latest OEE";
            break;
        case "historic avg":
            oeeTypeLabel = `Historic OEE Avg (${oeeAvgCount} POs)`;
            break;
        case "manual":
            oeeTypeLabel = "Manual OEE";
            break;
        case "default":
        default:
            oeeTypeLabel = "Default OEE";
            break;
    }


    //console.log ("oeeType", oeeType);
    //console.log ("oeeTypeLabel", oeeTypeLabel   );
    //console.log("props.operation?.oeeCalculationType", props.operation?.oeeCalculationType);
    //console.log("props.operation", props.operation);



    return (
        <>
            {/* <AxionControls.AxionBackdrop open={props.showBackdrop} /> */}

            <Box width="auto" sx={{ p: 1, m: 1 }}>

                <Stack direction="row" spacing={1} sx={{ marginTop: 0 }}>
                    

                    <Box>

                        <Stack direction="column" display="flex" alignItems="flex-start" alignContent="flex-start" justifyContent="flex-start" sx={{

                            //display: "grid",
                            // gridTemplateColumns: "repeat(2, 1fr)",
                            // gap: 1,
                            padding: "10px",
                            marginTop: "16px",
                            border: "1pt solid #666666",

                        }}
                        >
                            {/* <Box sx={{ width: 300, marginLeft: 0, }}>
                    Updating order: {props?.operation?.productionOrderId}
                </Box> */}
                            <Stack direction="row" spacing={2} sx={{ marginTop: 0 }}>
                                <Box sx={{ width: "120px", marginLeft: 0 }}><FormattedMessage {...messages.updPplLabelUpdatingOrder} />:</Box>
                                <Box>{props?.operation?.productionOrderId}</Box>
                            </Stack>
                            <Stack direction="row" spacing={2} sx={{ marginTop: 1 }}>
                                <Box sx={{ width: "120px", marginLeft: 0 }}><FormattedMessage {...messages.updPplLabelProduct} />:</Box>
                                <Box>{productId}</Box>
                            </Stack>
                            <Stack direction="row" spacing={2} sx={{ marginTop: 1 }}>
                                <Box sx={{ width: "120px", marginLeft: 0 }}><FormattedMessage {...messages.newPplLabelToolId} />:</Box>
                                <Box>{toolId}</Box>
                            </Stack>
                            <Stack direction="row" spacing={2} sx={{ marginTop: 1 }}>
                                <Box sx={{ width: "120px", marginLeft: 0 }}><FormattedMessage {...messages.updPplLabelCurrentMachine} />:</Box>
                                <Box>{machineName}</Box>
                            </Stack>

                        </Stack>
                        {/* <Box sx={{ marginTop: 2 }}>Machine</Box> */}
                        <Box sx={{ width: 300, marginLeft: 0, marginTop: 2 }}>

                            <Select
                                name="Machinex"
                                label={formatMessage(messages.newPplLabelMachine)}
                                sx={{ width: "300px" }}
                                width="300px"
                                // initialValue={machineId || ""}
                                // value={machineId || ""}
                                initialValue={getMachineNameById(machineId) || ""}
                                value={getMachineNameById(machineId) || ""}
                                onChange={handleMachineChange}
                            // onChange={(event) => {
                            //     setMachineId(event.target.value);
                            //     // console.log("Selected machine:", event.target.value);
                            // }}
                            >
                                {itemMachines?.map((machine) => (
                                    <MenuItem key={machine.id} value={machine.machineName}>
                                        {machine.abC_Class ? `${machine.abC_Class} - ${machine.machineName}` : machine.machineName}
                                    </MenuItem>
                                ))}
                            </Select>
                            {/* 
                    <Select
                        name="Machine"
                        label="Machine"
                        sx={{ width: "300px" }}
                        width="300px"
                        disabled={false}
                        initialValue={getMachineNameById(machineId) || ""}
                        value={getMachineNameById(machineId) || ""}
                        onChange={handleMachineChange}
                    >
                        {itemMachines?.map((m) => (
                            <MenuItem key={m.id} value={m.machineName}>
                                {m.abC_Class} - {m.machineName}
                            </MenuItem>
                        ))}
                    </Select> */}


                        </Box>

                        <Box sx={{ width: 300, marginLeft: 0, marginTop: 2 }}>
                            <FormControl>
                                <InputLabel id="oeeTypeLabel">OEE Type</InputLabel>
                                <Select
                                    name="OeeType"
                                    label="OeeType"
                                    sx={{ width: "300px" }}
                                    width="300px"
                                    disabled={false}
                                    value={oeeType || ""}
                                    onChange={(event, newValue) => handleOeeTypeChange(event, newValue)}
                                >
                                    {oeeHistoric !== null &&
                                        <MenuItem key={0} value={"historic"}>
                                            {tak !== null ? `${(oeeHistoric * 100).toFixed(0)} %` : ""} - Latest OEE
                                        </MenuItem>
                                    }
                                    {oeeAvg !== null &&
                                        <MenuItem key={1} value={"historic avg"}>
                                            {(oeeAvg * 100).toFixed(0)} % - Historic OEE Avg ({oeeAvgCount} POs)
                                        </MenuItem>
                                    }
                                    <MenuItem key={2} value={"default"}>
                                        {defaultOee !== null ? `${(defaultOee * 100).toFixed(0)} %` : ""} - Default OEE
                                    </MenuItem>
                                    <MenuItem key={3} value={"manual"}>
                                        Manual OEE
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ width: 300, marginLeft: 0, marginTop: 2, height: "50px", display: "flex", alignItems: "center" }}>
                            {showManualOeeInput && (
                                <TextField
                                    name="ManualOee"
                                    label="Manual OEE %"
                                    value={manualOee !== null ? (manualOee * 100).toFixed(0) : ""}
                                    onChange={handleManualOeeChange}
                                />
                            )}
                        </Box>

                        {/* <Box sx={{ marginTop: 2 }}>Start time</Box> */}
                        <Box sx={{ width: 300, marginLeft: 0, marginTop: 2 }}>
                            <GridDateTimePicker
                                label={formatMessage(messages.newPplLabelStartDate)}
                                labelTime={formatMessage(messages.newPplLabelStartTime)}
                                locale={language}
                                maxDate=""
                                minDate={Date().toLocaleString()}
                                onChange={handleStartTimeChange}
                                onDirectChange={handleStartTimeChange}
                                date={startTime}
                                width="100%"
                            />
                        </Box>

                        <Box sx={{ width: "300px", marginLeft: 0 }}>
                                        <TextField
                                            name="quantity"
                                            label={formatMessage(messages.newPplLabelQuantity)}
                                            value={quantity}
                                            onChange={handleQuantityChange}
                                            sx={{ mt: 1, mb: 1, width: "300px" }}
                                        />
                                    </Box>
                    </Box>

                    <Box height="100%" >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: 1,
                                padding: "10px",
                                marginTop: "16px",
                                border: "1pt solid #666666"
                            }}
                        >
                            {/* Align value box to the right */}
                            <Box sx={{ width: "110px" }} ><FormattedMessage {...messages.newPplLabelHistoricOee} /></Box><Box sx={{ textAlign: "center", backgroundColor: historicOeeFound ? "#799c4a" : "#cc4a4a" }}>{historicOeeFound ? formatMessage(messages.newPplLabelYes) : formatMessage(messages.newPplLabelNo)}</Box>
                            <Box sx={{ width: "110px" }}><FormattedMessage {...messages.newPplLabelOee} />:</Box><Box sx={{ textAlign: "right" }}>{(tak * 100).toFixed(0)} %</Box>
                            <Box sx={{ width: "110px" }}>Ideal cycle time:</Box><Box sx={{ textAlign: "right" }}>{idealCycleTime}</Box>
                            {/* <Box sx={{ width: "110px" }}>OEE Type:</Box><Box sx={{ textAlign: "right" }}>{oeeTypeLabel}</Box> */}
                            <Box sx={{ width: "110px", marginTop: "10px" }}><FormattedMessage {...messages.newPplLabelStockBalance} />:</Box><Box sx={{ textAlign: "right", marginTop: "10px" }}>{stockBalance}</Box>
                            <Box sx={{ width: "110px" }}><FormattedMessage {...messages.newPplLabelReservedBalance} />:</Box><Box sx={{ textAlign: "right" }}>{reservedBalance}</Box>
                            {/* <Box sx={{ width: "110px" }}><FormattedMessage {...messages.newPplLabelToolId} />:</Box><Box sx={{ textAlign: "right" }}>{toolId}</Box> */}
                        </Box>
                    </Box>

                </Stack>


                <Box sx={{ width: 500, marginLeft: 0, marginTop: 2 }}>
                    <Stack display="flex" direction="row" alignContent="center" alignItems="center" spacing={1}>
                        <Switch fontSize="small" onChange={handleSnapChange} checked={props.snapToPrevious === "true" ? true : false}
                            sx={{

                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: "#799c4a !important",
                                    '&:hover': {
                                        backgroundColor: alpha("#799c4a", theme.palette.action.hoverOpacity),
                                    },
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: "#799c4a !important",
                                },
                            }}
                        />
                        <Box><FormattedMessage {...messages.newPplLabelSwitchSnap} /></Box>
                    </Stack>
                    <Stack display="flex" direction="row" alignContent="center" alignItems="center" spacing={1}>
                        <Switch fontSize="medium" onChange={handleReplanChange} checked={props.replanOverlaps === "true" ? true : false}
                            sx={{

                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: "#799c4a !important",
                                    '&:hover': {
                                        backgroundColor: alpha("#799c4a", theme.palette.action.hoverOpacity),
                                    },
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: "#799c4a !important",
                                },
                            }}
                        />
                        <Box><FormattedMessage {...messages.newPplLabelSwitchReplan} /></Box>
                    </Stack>
                    {/* <Stack display="flex" direction="row" alignContent="center" alignItems="center" spacing={1}>
                        <Switch fontSize="medium" onChange={handleRecalculateChange} checked={props.recalculateAffectedOrders === "true" ? true : false}
                            sx={{

                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: "#799c4a !important",
                                    '&:hover': {
                                        backgroundColor: alpha("#799c4a", theme.palette.action.hoverOpacity),
                                    },
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: "#799c4a !important",
                                },
                            }}
                        />
                        <Box><FormattedMessage {...messages.newPplLabelSwitchRecalculate} /></Box>
                    </Stack> */}
                </Box>

                <Box sx={{ width: 500, marginLeft: 3, marginTop: 2, paddingTop: 1 }}>
                    <Stack display="flex" direction="row" alignContent="flex-end" alignItems="flex-end" spacing={1} height="20px">
                        {props.replanOverlaps === "true" ?
                            <>
                                <Stack direction="row" spacing={1} alignItems="flex-start" justifyItems="flex-start" >
                                    <WarningAmberOutlinedIcon fontSize="medium" sx={{ width: "34px" }} />
                                    <Box marginLeft={1}><FormattedMessage {...messages.newPplLabelWarnOverlap} /></Box>
                                </Stack>
                            </>
                            : <Box>  </Box>
                        }
                    </Stack>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" justifyItems="center" marginTop={2}>
                    <Tooltip title={<Typography>
                        Starttime: {dayjs(startTime).format(isoFormat)} <br />
                        Idealcycletime: {props.operation.idealCycleTime}<br />
                        ItemOee: {tak}<br />
                        PlannedQty: {props.operation.plannedQty}<br />
                        ReportedQty: {props.operation.reportedQty}<br />
                        Speedunittypeid: {speedUnitTypeId}<br />
                        IdealTimeToEnd: {idealTimeToEnd} minutes, {idealTimeToEnd / 60} hours<br />
                        EndTime : {dayjs(startTime).add(idealTimeToEnd, 'minute').format(isoFormat)}<br />
                        Calculation:<br />

                        idealTimeToEnd_cycletime = {props.operation.idealCycleTime} * ({props.operation.plannedQty} - {props.operation.reportedQty}) / {tak} / 60  minutes<br />
                        idealTimeToEnd_cycletime = {idealCycleTime} * {quantity} / {tak} / 60 = {idealCycleTime * quantity / tak / 60}  minutes

                        idealTimeToEnd_kg/h =  ({props.operation.plannedQty} - {props.operation.reportedQty}) / {idealCycleTime} * 60 / {tak} = {(props.operation.plannedQty - props.operation.reportedQty) / idealCycleTime * 60 / tak}  minutes

                    </Typography>} fontSize="medium"><InfoOutlined></InfoOutlined></Tooltip>
                    <Box
                        sx={{
                            //display: "grid",
                            //gridTemplateColumns: "repeat(1, 1fr)",
                            gap: 1,
                            padding: "10px",
                            marginTop: "16px",
                            border: "1pt solid #666666"
                        }}
                    >
                        {/* Align value box to the right */}
                        <Box sx={{ width: "250px" }} >{formatMessage(messages.newPplLabelIdealRuntime)}: {getHourAndMinutesFromMinutes(localIdealTimeToEnd).hours + " " + formatMessage(messages.newPplLabelHours) + ", " + getHourAndMinutesFromMinutes(localIdealTimeToEnd).minutes + " " + formatMessage(messages.newPplLabelMinutes)}</Box>
                    </Box>
                </Stack>



            </Box>



            {/* Move this to the far right */}

            <Button
                variant="contained"
                sx={{
                    marginTop: 2,
                    marginLeft: "auto",
                    display: "block",
                    width: "140px",
                }}
                onClick={handleUpdatePO}>
                {formatMessage(messages.updPplBtnUpdateOrder)}
            </Button>
        </>

    );
}